import { useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import type { HttpEndpoint } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import type { RouteLocation } from 'vue-router'

import { ROUTES } from '../route-names'

import { useRequests } from './useRequests'
import translations from './useReviewsTitle.translations'

type Title = Record<string, string>

// TODO Adapt to dedicated service signature once it has been created
function useFetchTitle(route: RouteLocation) {
  const requests = useRequests()
  const routeName = route.name?.toString() ?? ''

  if (!routeName || !requests || !('getTitle' in requests))
    return { data: ref(''), error: ref() }

  const requestOptionsByRoute = {
    [ROUTES.PAGES.PRODUCT]: {
      pathParams: { productId: route.params.uuid },
      transform: ({ title }: Title) => title,
    },
    [ROUTES.PAGES.LANDING]: {
      queryParams: { id: route.params.uuid },
      transform: ({ displayedTitle }: Title) => displayedTitle,
    },
    [ROUTES.PAGES.MERCHANT]: {
      pathParams: { id: route.params.uuid },
      transform: ({ company }: Title) => company,
    },
  }

  return useHttpFetch(
    requests.getTitle as HttpEndpoint<Title>,
    requestOptionsByRoute[routeName] || {},
  )
}

export function useReviewsTitle() {
  const route = useRoute()
  const logger = useLogger()
  const i18n = useI18n()

  const { data: shortTitle, error } = useFetchTitle(route)

  watch(
    error,
    () => {
      if (error.value instanceof Error) {
        logger.error(`[REVIEWS][${route.name?.toString()}]`, {
          error: error.value,
        })
      }
    },
    { immediate: true },
  )

  const mainTitle = computed(() => {
    if (typeof shortTitle.value !== 'string') {
      return i18n(translations.titlePlaceholder)
    }

    switch (route.name) {
      case ROUTES.PAGES.PRODUCT:
        return i18n(translations.titleProduct, { product: shortTitle.value })

      case ROUTES.PAGES.MERCHANT:
        return i18n(translations.titleMerchantProducts, {
          merchant: shortTitle.value,
        })

      case ROUTES.PAGES.LANDING:
        return i18n(translations.titleListingProduct, {
          listing: shortTitle.value,
        })

      case ROUTES.PAGES.ALL:
        return i18n(translations.titleAllProducts)

      default:
        return i18n(translations.titlePlaceholder)
    }
  })

  return {
    mainTitle,
    shortTitle,
  }
}
